import { useCallback, useEffect, useRef } from "react"
import { useGetAssetQuery } from "~/ui-rtk/api/assetsApi"
import { useTagCategories } from "~/ui-rtk/hooks/use-structured-tags"
import {
  mergeSuggestedSelection,
  suggestedTagsOf,
  suggestionsToSelection,
} from "./tagSuggestions"

/**
 * When an asset lands on the ad and its creative analysis left tag picks,
 * apply them to the ad's tags — once per asset, and only into categories the
 * user has not picked yet. The picks come from the asset the builder holds
 * (library assets carry their metadata) or, for one just uploaded, from the
 * asset row once the forecast has analysed it: call `refresh` then.
 */
export function useAssetTagSuggestions({
  assetId,
  metadata,
  current,
  skipAssetId,
  onApply,
}: {
  /** The ad's first asset. */
  assetId?: string
  /** That asset's metadata as the builder holds it (may predate the analysis). */
  metadata?: Record<string, unknown> | null
  /** The ad's current tag selection (category slug → option ids). */
  current: Record<string, string[]>
  /** An asset that must not auto-tag: the one a saved draft opened with. */
  skipAssetId?: string
  onApply: (
    next: Record<string, string[]>,
    filled: Array<{ slug: string; label: string }>,
  ) => void
}) {
  const { categories } = useTagCategories()
  const local = suggestedTagsOf(metadata)
  // Only go to the server when the builder's copy of the asset has no picks.
  const { data: fetched, refetch } = useGetAssetQuery(assetId ?? "", {
    skip: !assetId || local.length > 0,
  })
  const suggestions =
    local.length > 0
      ? local
      : suggestedTagsOf(fetched?.id === assetId ? fetched?.metadata : null)

  const appliedForRef = useRef<string | undefined>(undefined)
  const currentRef = useRef(current)
  currentRef.current = current
  const onApplyRef = useRef(onApply)
  onApplyRef.current = onApply

  useEffect(() => {
    if (!assetId || !categories?.length) return
    if (assetId === skipAssetId) {
      appliedForRef.current = assetId
      return
    }
    if (appliedForRef.current === assetId || suggestions.length === 0) return
    const selection = suggestionsToSelection(suggestions, categories)
    const { next, filled } = mergeSuggestedSelection(
      currentRef.current,
      selection,
    )
    appliedForRef.current = assetId
    if (filled.length === 0) return
    onApplyRef.current(
      next,
      filled.map(slug => ({
        slug,
        label: categories.find(c => c.slug === slug)?.label ?? slug,
      })),
    )
  }, [assetId, skipAssetId, suggestions, categories])

  /** Re-read the asset row (after the analysis job wrote the picks). */
  const refresh = useCallback(() => {
    if (assetId && local.length === 0) void refetch()
  }, [assetId, local.length, refetch])

  return { refresh }
}
